import { CONFIG } from "./config";

export interface Vec2 {
  x: number;
  y: number;
}

/**
 * PositionHistory — authoritative 60 Hz player-route ring buffer (spec §5.4).
 * Sample k holds the player position at simulation time k/60.
 * Echo positions are linear interpolations of this buffer; they are NEVER
 * derived from rendered nodes.
 */
export class PositionHistory {
  private readonly capacity = CONFIG.historyCapacity;
  private readonly xs = new Float64Array(CONFIG.historyCapacity);
  private readonly ys = new Float64Array(CONFIG.historyCapacity);
  /** Total samples ever pushed; sample index i lives at i % capacity. */
  count = 0;

  push(x: number, y: number): void {
    const slot = this.count % this.capacity;
    this.xs[slot] = x;
    this.ys[slot] = y;
    this.count++;
  }

  sampleAt(index: number): Vec2 | null {
    if (index < 0 || index >= this.count) return null;
    if (index < this.count - this.capacity) return null; // evicted
    const slot = index % this.capacity;
    return { x: this.xs[slot], y: this.ys[slot] };
  }

  /**
   * Interpolated route position at time `t` seconds (spec §19.4).
   * Returns null for t < 0 (echo does not exist yet) or evicted history.
   */
  positionAt(t: number): Vec2 | null {
    if (t < 0 || this.count === 0) return null;
    const idx = t * 60;
    let i = Math.floor(idx);
    const last = this.count - 1;
    if (i >= last) return this.sampleAt(last);
    const a = this.sampleAt(i);
    const b = this.sampleAt(i + 1);
    if (!a || !b) return null;
    const f = idx - i;
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
  }
}
